import type { ReactNode, SVGAttributes } from 'react';

type IconProps = Omit<SVGAttributes<SVGSVGElement>, 'children'> & {
  readonly size?: number;
};

const base = (size: number): SVGAttributes<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  overflow: 'visible',
  'aria-hidden': true,
  focusable: false,
});

// Shaft-less chevron for the back affordance. Heavier stroke than the
// shared 1.5 default so the lone "<" reads with enough weight to not look
// like a stray glyph at the top of the screen.
export const IconChevronLeft = ({ size = 24, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} strokeWidth={2} {...rest}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export const IconArrowDown = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest} className={['icon', rest.className].filter(Boolean).join(' ')}>
    <path d="M12 5v14" />
    <path d="M19 12l-7 7-7-7" />
  </svg>
);

export const IconSnowflake = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest} className={['icon', rest.className].filter(Boolean).join(' ')}>
    <path d="M12 2v20" />
    <path d="M2 12h20" />
    <path d="M5 5l14 14" />
    <path d="M19 5L5 19" />
  </svg>
);

export const IconList = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest} className={['icon', rest.className].filter(Boolean).join(' ')}>
    <path d="M8 6h13" />
    <path d="M8 12h13" />
    <path d="M8 18h13" />
    <circle cx="4" cy="6" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="4" cy="12" r="0.6" fill="currentColor" stroke="none" />
    <circle cx="4" cy="18" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

export const IconSettings = ({ size = 16, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const IconUser = ({ size = 18, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
  </svg>
);

export const IconClose = ({ size = 16, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export const IconCopy = ({ size = 15, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

export const IconSearch = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const IconFilter = ({ size = 17, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <path d="M3 6h18" />
    <path d="M7 12h10" />
    <path d="M11 18h2" />
  </svg>
);

export const IconCheck = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const IconClock = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const IconSupport = ({ size = 22, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <path d="M2 16a2 2 0 0 1 2-2h1v5H4a2 2 0 0 1-2-2z" />
    <path d="M22 16a2 2 0 0 0-2-2h-1v5h1a2 2 0 0 0 2-2z" />
    <path d="M19 19a3 3 0 0 1-3 3h-2" />
  </svg>
);

export const IconShield = ({ size = 22, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <path d="M12 3l7 3v6c0 4-3 7.5-7 9-4-1.5-7-5-7-9V6z" />
    <path d="M9.5 12.5l1.8 1.8 3.4-3.6" />
  </svg>
);

export const IconLogout = ({ size = 22, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
// Add this export to src/components/icons.tsx (append at the end of the file)

export const IconArrowUp = ({ size = 14, ...rest }: IconProps): ReactNode => (
  <svg {...base(size)} {...rest} className={['icon', rest.className].filter(Boolean).join(' ')}>
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </svg>
);
